import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Icon from '@amzn/awsui-components-core/icon';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faStar as solidStar, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateOrUpdateApplicationFavoritesMutation } from 'src/API';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import DFP_LOGO from 'src/images/FinSuite-icon.svg';
import { logger } from 'src/logger';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';
import { useNavigationItems } from '../side-navigation-v2/useNavigationItems';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import './GlobalSearchBar.scss';
import { GroupedSearchResults, MAX_RESULTS_PER_CATEGORY, performSearch, QuickLinkItem, SearchResultItem } from './GlobalSearchUtils';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const buildQuickLinks = (navItems: ReturnType<typeof useNavigationItems>): QuickLinkItem[] => {
  const links: QuickLinkItem[] = [];
  let currentSection = '';

  navItems.forEach((item) => {
    if (item.type === 'section-header') {
      currentSection = item.text;
    } else if (item.type === 'link' && item.href) {
      links.push({ id: `nav-${item.href}`, text: item.text, href: item.href, external: item.external, section: currentSection || 'Navigation' });
    }
  });

  PROFILE_DROPDOWN_ITEMS.forEach((group) => {
    if ('items' in group && group.items) {
      group.items.forEach((item) => {
        if ('href' in item && item.href && item.text) {
          links.push({ id: `profile-${item.id}`, text: item.text, href: item.href, external: true, section: 'Quick Links' });
        }
      });
    } else if ('href' in group && group.href && group.text) {
      links.push({ id: `profile-${group.id}`, text: group.text, href: group.href, external: true, section: 'Quick Links' });
    }
  });

  return links;
};

/** Escape special regex characters in a string */
const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Highlight matching query text within a string, returning React nodes */
const highlightText = (text: string, searchQuery: string): React.ReactNode => {
  const trimmed = searchQuery.trim();
  if (!trimmed || !text) return text;
  const regex = new RegExp(`(${escapeRegex(trimmed)})`, 'gi');
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="global-search__highlight">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

/** Strip HTML tags to get plain text for safe highlighting */
const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '');

/** Flagship application names — curated list of key products */
const FLAGSHIP_APP_NAMES = new Set([
  'Prophecy',
  'Vista',
  'Verity Legacy',
  'Verity',
  'xPT',
  'Subscription',
  'Yeti',
  'Central Data Platform',
  'Ask Rino Chatbot',
  'Axis',
  'Quest',
  'EA Huddle'
]);

/** Build filter tag counts from matched products */
interface ProductFilterTag {
  label: string;
  count: number;
}

const buildProductFilterTags = (products: SearchResultItem[]): ProductFilterTag[] => {
  // Flagship count
  const flagshipCount = products.filter((p) => FLAGSHIP_APP_NAMES.has(p.title)).length;

  // Favorites count
  const favoritesCount = products.filter((p) => p.isFavorite).length;

  // GL account counts
  const counts: Record<string, number> = {};
  products.forEach((p) => {
    const gl = p.glAccount || 'Other';
    counts[gl] = (counts[gl] || 0) + 1;
  });

  const glTags = Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, count]) => ({ label, count }));

  const tags: ProductFilterTag[] = [];

  // Always add Flagship first (only if there are matches)
  if (flagshipCount > 0) tags.push({ label: 'Flagship', count: flagshipCount });

  // Add Favorites (only if there are matches)
  if (favoritesCount > 0) tags.push({ label: 'Favorites', count: favoritesCount });

  // Add GL account tags
  tags.push(...glTags);

  return tags;
};

// ─── Component ─────────────────────────────────────────────────────────────────

export const GlobalSearchBar: React.FC = () => {
  const navigate = useNavigate();
  const { Alias: userAlias } = useAuth();
  const { parsedApplications, favoriteApplications, setFavoriteApplications, announcementsFlatEntity, homePageContent } = useAppContext();
  const navItems = useNavigationItems();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGlAccount, setSelectedGlAccount] = useState('Flagship');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeParsedApps = useMemo(() => parsedApplications.filter((app) => app.isActive), [parsedApplications]);
  const quickLinks = useMemo(() => buildQuickLinks(navItems), [navItems]);
  const welcomeContent = useMemo(() => homePageContent?.welcomeContentDetails || [], [homePageContent]);
  const financeCalendarEvents = useMemo(() => homePageContent?.financeCalendarEvents || [], [homePageContent?.financeCalendarEvents]);

  const results: GroupedSearchResults = useMemo(
    () => performSearch(query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents),
    [query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents]
  );

  // Product filter tags for the Products section
  const glAccountTags = useMemo(() => buildProductFilterTags(results.products), [results.products]);

  // Filter products by selected tag (Flagship, Favorites, or GL account)
  const filteredProducts = useMemo(() => {
    if (selectedGlAccount === 'Flagship') return results.products.filter((p) => FLAGSHIP_APP_NAMES.has(p.title));
    if (selectedGlAccount === 'Favorites') return results.products.filter((p) => p.isFavorite);
    return results.products.filter((p) => (p.glAccount || 'Other') === selectedGlAccount);
  }, [results.products, selectedGlAccount]);

  const hasResults = results.products.length > 0 || results.announcements.length > 0 || results.pages.length > 0;
  const showDropdown = isOpen && query.trim().length > 0;

  // Reset filter when query changes
  useEffect(() => {
    setSelectedGlAccount('Flagship');
  }, [query]);

  // Global keyboard shortcut: Option+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && (e.key.toLowerCase() === 's' || e.key === 'ß' || e.code === 'KeyS')) {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ─── Event handlers ────────────────────────────────────────────────────────

  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      setIsOpen(false);
      setQuery('');

      if (item.category === 'announcements') {
        navigate('/announcements', { state: { filterByTitle: item.title } });
        return;
      }

      if (item.id.startsWith('calendar-')) {
        navigate('/', { state: { highlightCalendarEvent: { scenario: item.meta, event: item.title } } });
        return;
      }

      // Products: embedded → SPA navigate, external → new tab
      if (item.category === 'products') {
        if (item.embeddingEnabled && item.href) {
          navigate(item.href);
        } else if (item.href) {
          window.open(item.href, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      // Pages: external → new tab, internal → navigate
      if (item.external) {
        if (item.href) window.open(item.href, '_blank', 'noopener,noreferrer');
        return;
      }

      if (item.href?.startsWith('/')) {
        navigate(item.href);
      } else if (item.href) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate]
  );

  const toggleFavorite = useCallback(
    async (e: React.MouseEvent, item: SearchResultItem) => {
      e.stopPropagation();
      const isCurrentlyFavorite = favoriteApplications.includes(item.id);
      const updatedFavorites = isCurrentlyFavorite ? favoriteApplications.filter((id) => id !== item.id) : [...favoriteApplications, item.id];

      logger.info(`DaS FinSuite -> User Action: ${isCurrentlyFavorite ? 'Unfavorite' : 'Favorite'} App from Search`, {
        event_type: isCurrentlyFavorite ? UserActionsEventTypes.TOP_NAV_UNFAVORITE_APP : UserActionsEventTypes.TOP_NAV_FAVORITE_APP,
        value: item.title
      });

      setFavoriteApplications(updatedFavorites);

      try {
        await fetchGraphQLRequest<CreateOrUpdateApplicationFavoritesMutation>({
          query: mutations.createOrUpdateApplicationFavorites,
          variables: { input: { userAlias, applicationId: updatedFavorites } },
          requestType: appSyncOperationName.MUTATION,
          requestName: 'createOrUpdateApplicationFavorites'
        });
      } catch (error: any) {
        logger.error('Error updating favorites from search', error);
        setFavoriteApplications(favoriteApplications);
      }
    },
    [favoriteApplications, setFavoriteApplications, userAlias]
  );

  const handleShowMore = useCallback(
    (section: 'products' | 'announcements') => {
      setIsOpen(false);
      setQuery('');
      if (section === 'products') navigate('/all-apps');
      else navigate('/announcements');
    },
    [navigate]
  );

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderProductIcon = (item: SearchResultItem) => {
    if (item.embeddingEnabled) {
      return (
        <span className="global-search__embedded-icon" aria-label="Opens within FinSuite">
          <img src={DFP_LOGO} alt="" />
        </span>
      );
    }
    return (
      <span className="global-search__external-icon" aria-label="Opens in new tab">
        <FontAwesomeIcon icon={faExternalLinkAlt} />
      </span>
    );
  };

  const renderProductsSection = () => {
    if (results.products.length === 0) return null;
    const displayItems = filteredProducts.slice(0, MAX_RESULTS_PER_CATEGORY);
    const hasMore = filteredProducts.length > MAX_RESULTS_PER_CATEGORY;

    return (
      <div className="global-search__category">
        <div className="global-search__category-header">
          <span className="global-search__category-title">Products</span>
          {hasMore && (
            <button className="global-search__show-more" onClick={() => handleShowMore('products')}>
              Show more
            </button>
          )}
        </div>

        {/* GL Account filter tags */}
        {glAccountTags.length > 2 && (
          <div className="global-search__tags">
            {glAccountTags.map((tag) => (
              <span
                key={tag.label}
                className="global-search__tag"
                onClick={() => setSelectedGlAccount(tag.label)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedGlAccount(tag.label);
                  }
                }}
              >
                <Badge
                  color={selectedGlAccount === tag.label ? 'blue' : 'grey'}
                  style={
                    selectedGlAccount !== tag.label
                      ? { root: { background: 'transparent', borderColor: '#8d99a8', borderWidth: '1px', color: '#c9d1d9' } }
                      : undefined
                  }
                >
                  {tag.label} • {Math.min(tag.count, MAX_RESULTS_PER_CATEGORY)}
                </Badge>
              </span>
            ))}
          </div>
        )}

        {displayItems.map((item) => (
          <div key={`products-${item.id}`} className="global-search__result-item" onClick={() => handleResultClick(item)}>
            <div className="global-search__result-content">
              <div className="global-search__result-title">
                <span>{highlightText(item.title, query)}</span>
              </div>
              {item.description && (
                <div className="global-search__result-description">
                  <p>{highlightText(stripHtml(item.description), query)}</p>
                </div>
              )}
            </div>
            <div className="global-search__result-actions">
              <button
                className="global-search__favorite-btn"
                onClick={(e) => toggleFavorite(e, item)}
                aria-label={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <FontAwesomeIcon icon={item.isFavorite ? solidStar : regularStar} className={item.isFavorite ? 'global-search__star--active' : ''} />
              </button>
              {renderProductIcon(item)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderGenericSection = (items: SearchResultItem[], label: string, sectionKey: 'announcements' | 'pages') => {
    if (items.length === 0) return null;
    const displayItems = items.slice(0, MAX_RESULTS_PER_CATEGORY);
    const hasMore = items.length > MAX_RESULTS_PER_CATEGORY && sectionKey !== 'pages';

    return (
      <div className="global-search__category">
        <div className="global-search__category-header">
          <span className="global-search__category-title">{label}</span>
          {hasMore && (
            <button className="global-search__show-more" onClick={() => handleShowMore(sectionKey as 'announcements')}>
              Show more
            </button>
          )}
        </div>
        {displayItems.map((item) => (
          <div key={`${sectionKey}-${item.id}`} className="global-search__result-item" onClick={() => handleResultClick(item)}>
            <div className="global-search__result-content">
              <div className="global-search__result-title">
                <span>{highlightText(item.title, query)}</span>
              </div>
              {item.description && (
                <div className="global-search__result-description">
                  <p>{highlightText(stripHtml(item.description), query)}</p>
                </div>
              )}
              {item.meta && <div className="global-search__result-meta">{item.meta}</div>}
            </div>
            {(item.external || item.href) && (sectionKey === 'announcements' || item.external) && (
              <span className="global-search__external-icon" aria-label="Opens in new tab">
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="global-search" ref={containerRef}>
      <div className={`global-search__input-wrapper${showDropdown ? ' global-search__input-wrapper--active' : ''}`}>
        <Icon name="search" className="global-search__search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="global-search__input"
          placeholder="Search apps, links, and product launches..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (query.trim()) setIsOpen(true);
          }}
          aria-label="Global search"
          aria-expanded={showDropdown}
          role="combobox"
          autoComplete="off"
        />
        {!query && <span className="global-search__shortcut-hint">⌥S</span>}
        {query && (
          <button
            className="global-search__clear-btn"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <Icon name="close" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="global-search__dropdown">
          <div className="global-search__results">
            {!hasResults && (
              <Box padding="l" textAlign="center">
                <span className="global-search__no-results">No results found for &quot;{query}&quot;</span>
              </Box>
            )}
            {renderProductsSection()}
            {renderGenericSection(results.pages, 'Links', 'pages')}
            {renderGenericSection(results.announcements, 'Product Launches', 'announcements')}
          </div>
        </div>
      )}
    </div>
  );
};
